import { API } from "@/lib/api";

export const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.18em] ${className}`}>{children}</span>
);

const inpCls =
  "w-full bg-navy-950 border border-gold-500/20 px-3.5 py-3 text-white text-sm rounded-sm focus:outline-none focus:border-gold-500";

export function Field({ label, value, onChange, testid, type = "text", placeholder = "", required = false }) {
  return (
    <label className="block">
      <Mono className="text-white/55 text-[0.55rem] block mb-1.5">
        {label}
        {required && <span className="text-gold-400"> *</span>}
      </Mono>
      <input
        type={type}
        value={value ?? ""}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={inpCls}
      />
    </label>
  );
}

export function TextArea({ label, value, onChange, testid, rows = 3 }) {
  return (
    <label className="block">
      <Mono className="text-white/55 text-[0.55rem] block mb-1.5">{label}</Mono>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={inpCls + " resize-none"}
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options = [], testid, placeholder = "Select…" }) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <label className="block">
      <Mono className="text-white/55 text-[0.55rem] block mb-1.5">{label}</Mono>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inpCls}>
        <option value="" className="bg-navy-900">{placeholder}</option>
        {norm.map((o) => (
          <option key={o.value} value={o.value} className="bg-navy-900">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Btn({ children, onClick, type = "button", variant = "primary", disabled, testid, className = "" }) {
  const styles = {
    primary: "bg-gold-500 text-navy-950 font-semibold hover:bg-gold-400",
    ghost: "border border-gold-500/30 text-gold-300 hover:bg-gold-500/10",
    danger: "border border-red-500/40 text-red-300 hover:bg-red-500/10",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full py-3 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function photoSrc(p) {
  if (p.hasBlob && p.blob) return URL.createObjectURL(p.blob);
  const token = localStorage.getItem("mdau_token");
  return `${API}/fieldclaim/photos/${p.id}/file?token=${token}`;
}

// Lightweight, offline mirror of the backend NSWRA/QRA readiness rules.
export function assetReadiness(asset, photos) {
  const fm = (asset.funding_measure || "EPAR").toUpperCase();
  const recon = fm === "EPAR" || fm === "REPA" || fm === "IRW";
  const counts = { pre_disaster: 0, damage: 0, repair: 0, signage: 0 };
  photos.forEach((p) => counts[p.phase] != null && counts[p.phase]++);
  const total = photos.length;
  const noGps = photos.filter((p) => p.latitude == null).length;
  const classified = ["work_type", "road_type", "damage_type", "funding_measure"].every((k) => asset[k]);
  const checks = [
    ["Damage evidence photo", counts.damage >= 1],
    ...(recon ? [["Pre-disaster comparison photo", counts.pre_disaster >= 1], ["Repair / completion photo", counts.repair >= 1]] : []),
    ["GPS on every photo", total > 0 && noGps === 0],
    ["Classification complete", !!classified],
  ];
  const passed = checks.filter((c) => c[1]).length;
  return { pct: Math.round((passed / checks.length) * 100), checks, counts, total };
}
